import {
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Command } from "~/components/guides/components";
import { Control, Controller } from "react-hook-form";
import {
  SECURE_HOST_URL_PATTERN,
  helperTextStyles,
  ValidationMessage,
} from "~/pages/integrations/utils/integrationValidationUtils";
import { ServiceNowFormInput } from "~/pages/integrations/hosted-integrations/forms/servicenow/ServiceNowIntegrationForm/ServiceNowIntegrationForm";
import { FieldErrors } from "react-hook-form/dist/types/errors";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { PasswordField } from "~/components/Form/components/PasswordField";

interface ServiceNowFormStepOneProps {
  control: Control<ServiceNowFormInput>;
  errors: FieldErrors<ServiceNowFormInput>;
  isUpdateFlow: boolean;
  isValid: boolean;
}

export const ServiceNowFormStepOne = ({
  control,
  errors,
  isUpdateFlow,
  isValid,
}: ServiceNowFormStepOneProps) => {
  const theme = useTheme();

  return (
    <>
      {/* Step 1 */}
      <Box pb={4}>
        <Command
          number={1}
          options={{
            fontSize: { xs: 16 },
            dotColor: theme.palette.background.lightest,
          }}
        >
          Choose an integration name
        </Command>

        <Box sx={{ pl: 6 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, mt: 2 }}
          >
            Please choose a descriptive name that lets you easily identify your
            integration.
          </Typography>
          <Controller
            control={control}
            name="integrationName"
            rules={{
              required: "Integration name is required",
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                placeholder="Your integration name..."
                error={!!errors.integrationName}
                helperText={
                  errors.integrationName && (
                    <ValidationMessage error={errors.integrationName} />
                  )
                }
                sx={{
                  background: theme.palette.code.background,
                  borderRadius: 1,
                  color: "text.primary",
                  ...helperTextStyles,
                }}
              />
            )}
          />
        </Box>
      </Box>

      {/* Step 2 */}
      <Box pb={4}>
        <Command
          number={2}
          options={{
            fontSize: { xs: 16 },
            dotColor: theme.palette.background.lightest,
          }}
        >
          Configure ServiceNow integration credentials
        </Command>

        <Box sx={{ pl: 6 }}>
          <Box pb={3} pt={2}>
            <Controller
              control={control}
              name="instanceUrl"
              rules={{
                required: "ServiceNow instance URL is required",
                pattern: {
                  value: SECURE_HOST_URL_PATTERN,
                  message: "Invalid HTTPS URL",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="ServiceNow Instance URL"
                  placeholder="https://dev123456.service-now.com"
                  error={!!errors.instanceUrl}
                  helperText={
                    errors.instanceUrl && (
                      <ValidationMessage error={errors.instanceUrl} />
                    )
                  }
                  sx={{
                    background: theme.palette.code.background,
                    borderRadius: 1,
                    color: "text.primary",
                    ...helperTextStyles,
                  }}
                />
              )}
            />
          </Box>

          <Box pb={3}>
            <Controller
              control={control}
              name="clientId"
              rules={{
                required: "Client ID is required",
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Client ID"
                  error={!!errors.clientId}
                  helperText={
                    errors.clientId && (
                      <ValidationMessage error={errors.clientId} />
                    )
                  }
                  sx={{
                    background: theme.palette.code.background,
                    borderRadius: 1,
                    color: "text.primary",
                    ...helperTextStyles,
                  }}
                />
              )}
            />
          </Box>
        </Box>
      </Box>

      {/* Step 3 - Ticket Options */}
      <Box pb={4}>
        <Command
          number={3}
          options={{
            fontSize: { xs: 16 },
            dotColor: theme.palette.background.lightest,
          }}
        >
          Configure ticket options (optional)
        </Command>

        <Box sx={{ pl: 6, pb: 3, pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Choose whether to automatically create and close tickets
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Controller
                control={control}
                name="autoCreateTickets"
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    select
                    label="Auto-create tickets"
                    value={field.value ? "true" : "false"}
                    onChange={(e) => field.onChange(e.target.value === "true")}
                    sx={{
                      background: theme.palette.code.background,
                      borderRadius: 1,
                      color: "text.primary",
                      ...helperTextStyles,
                    }}
                  >
                    <MenuItem value="false">No</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                  </TextField>
                )}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Controller
                control={control}
                name="autoCloseTickets"
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    select
                    label="Auto-close tickets"
                    value={field.value ? "true" : "false"}
                    onChange={(e) => field.onChange(e.target.value === "true")}
                    sx={{
                      background: theme.palette.code.background,
                      borderRadius: 1,
                      color: "text.primary",
                      ...helperTextStyles,
                    }}
                  >
                    <MenuItem value="false">No</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                  </TextField>
                )}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 3 }}>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={!isValid}
          endIcon={<ChevronRightIcon />}
        >
          {isUpdateFlow ? "Update Integration" : "Create Integration"}
        </Button>
      </Box>
    </>
  );
};
